import React from "react";
import { Trash, Warning, X } from "@phosphor-icons/react";

/**
 * Reusable destructive-action confirmation modal.
 * Replaces `window.confirm()` with a styled in-app dialog.
 *
 * Usage:
 *   <ConfirmDialog
 *     open={!!target}
 *     title="Delete this offer?"
 *     description={<>Permanently removes <strong>{target.title}</strong>...</>}
 *     onCancel={() => setTarget(null)}
 *     onConfirm={performDelete}
 *     busy={busy}
 *     error={err}
 *   />
 */
export default function ConfirmDialog({
  open,
  title = "Are you sure?",
  description,
  confirmLabel = "Yes, delete",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  busy = false,
  error = "",
  testId = "confirm-dialog",
  tone = "danger",
}) {
  if (!open) return null;
  const accent = tone === "danger" ? "#af3fa6" : "#dda632";
  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6"
      onClick={() => !busy && onCancel?.()}
      data-testid={testId}
    >
      <div
        className="bg-[#141e32] rounded-2xl w-full max-w-md p-8"
        style={{ border: `1px solid ${accent}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4 mb-5">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
            style={{ background: `${accent}26` }}
          >
            <Warning size={24} weight="fill" style={{ color: accent }} />
          </div>
          <div>
            <div className="font-display text-2xl tracking-tight mb-2">{title}</div>
            <div className="text-[#a8b2cc] text-sm leading-relaxed">{description}</div>
          </div>
        </div>
        {error && (
          <div
            className="rounded-lg bg-[#070b14] p-3 text-sm mb-4"
            style={{ border: `1px solid ${accent}`, color: accent }}
            data-testid={`${testId}-error`}
          >
            {error}
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            disabled={busy}
            className="btn-outline disabled:opacity-60"
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-white transition-colors font-nav text-xs uppercase tracking-[0.18em] font-semibold disabled:opacity-60"
            style={{ background: accent }}
            data-testid={`${testId}-confirm`}
          >
            <Trash size={14} weight="bold" />
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
